// Center zone: event tabs, event header, the main video canvas, the lane timeline, and (Phase 2) the parked dock.
// Mirrors the prototype's renderWork / playerHTML / timelineHTML, with the agreed differences in design/README.md
// (2026-10-16): no alarm chip row (the timeline is the spine, Timeline.tsx), no camera rail (the canvas takes the
// width; all cameras are in the pop-out matrix, Matrix.tsx). 2026-10-28: the header is one line (no customer /
// address / opened-by / counts sub-line) and there is no AI strip under it; the agent is in the Agent tab.
// Phase 5: the push-to-talk indicator over the canvas (prototype .ptt-live) and "Pop out" (the canvas in its own
// window, VideoPopout.tsx).

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, errorText, parkReasonLabel, type EventCamera, type EventSummary, type Watch } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { CameraFeed } from '../components/VideoPlayer.tsx';
import { useNow } from '../hooks.ts';
import { fmtTime, tzAbbr } from '../time.ts';
import { AiStatePill, VerdictChip } from './Agent.tsx';
import type { Ctl } from './ctl.ts';
import { SiteClock, Since } from './Ticks.tsx';
import { alarmMs, camShort, dur, human, ms, plural, SEV_CLS, SEV_LABEL } from './util.ts';
import { toIso, vtime } from './view.ts';
import { quadCameras, rankCameras } from './cameraRank.ts';
import { Timeline } from './Timeline.tsx';
import { watchLive, watchWhat } from './ai.ts';

/* ---------- event tabs (Phase 2: up to the cap, raised marker, [ ] to switch) ---------- */

export interface TabItem {
  e: EventSummary;
  /** Raised back to me and not opened yet: the tab pulses. */
  raised: boolean;
  /** Alarms that joined while the tab was in the background. */
  newAlarms: number;
}

export function EventTabs({
  tabs,
  currentId,
  viewing,
  label,
  parkedCount,
  activeCount,
  maxActive,
  onSelect,
}: {
  tabs: TabItem[];
  currentId: string | null;
  /** The open event when it is not one of my tabs (read-only view). */
  viewing: EventSummary | null;
  label?: string;
  parkedCount: number;
  activeCount: number;
  maxActive: number;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="evtabs" role="tablist" aria-label="My events ([ and ] switch)">
      {tabs.map(({ e, raised, newAlarms }, i) => {
        const on = e.id === currentId;
        return (
          <button
            key={e.id}
            type="button"
            role="tab"
            aria-selected={on}
            className={`evtab s-${SEV_CLS[e.severity]}${on ? ' on' : ''}${raised ? ' raised' : ''}`}
            onClick={() => onSelect(e.id)}
            title={`${e.displayNo} · ${e.siteName}${raised ? ' · raised back to you' : ''}${newAlarms ? ` · ${newAlarms} new alarm${newAlarms > 1 ? 's' : ''}` : ''} · tab ${i + 1}`}
          >
            <span className="dot" aria-hidden="true" />
            {e.siteName.split(' · ')[0]}
            {e.ai?.verdict && <VerdictChip ai={e.ai} size="sm" conf={false} />}
            {e.agentPaused && <span className="aipause" title="AI agent paused on this event">II</span>}
            <Since from={e.firstAlarmAt} className="mono dim" />
            {raised && <span className="rtag">raised</span>}
            {newAlarms > 0 && !on && <span className="tabnew">+{newAlarms}</span>}
          </button>
        );
      })}
      {viewing && (
        <button type="button" role="tab" aria-selected="true" className={`evtab on s-${SEV_CLS[viewing.severity]}`}>
          <span className="dot" aria-hidden="true" />
          {viewing.siteName.split(' · ')[0]}
          {viewing.ai?.verdict && <VerdictChip ai={viewing.ai} size="sm" conf={false} />}
          <Since from={viewing.firstAlarmAt} className="mono dim" />
          <span className={`ro-tag${viewing.state === 'agent_working' || viewing.state === 'settling' ? ' ai-t' : ''}`}>
            {viewing.state === 'parked' ? 'parked' : viewing.state === 'agent_working' ? 'AI triaging' : viewing.state === 'settling' ? 'auto-resolved' : 'read-only'}
          </span>
        </button>
      )}
      {!tabs.length && !viewing && label && <span className="evtab ghost">{label}</span>}
      <span className="sp" />
      <span className="cap">
        {parkedCount > 0 && `${parkedCount} parked · `}
        {activeCount}/{maxActive} active
      </span>
    </div>
  );
}

/* ---------- parked dock ---------- */

function DockChip({ e, open, canResume, busy, watches, onOpen, onResume }: { e: EventSummary; open: boolean; canResume: boolean; busy: boolean; watches?: Watch[]; onOpen: () => void; onResume: () => void }) {
  const now = useNow(1000);
  const fb = ms(e.park?.fallbackAt);
  const left = fb === null ? null : fb - now;
  const live = (watches ?? []).filter(watchLive);
  const hit = (watches ?? []).find((w) => w.status === 'hit');
  const wEnd = live.length ? Math.min(...live.map((w) => ms(w.expiresAt) ?? now)) : null;
  return (
    <div className={`dchip s-${SEV_CLS[e.severity]}${open ? ' open' : ''}${e.raised ? ' hit' : ''}`} role="listitem">
      <Icon name="park" />
      <button type="button" className="linkish dsite" onClick={onOpen} title={`${e.displayNo} · open read-only`}>
        {e.siteName.split(' · ')[0]}
      </button>
      <span className="sev">{parkReasonLabel(e.park?.reason).toUpperCase()}</span>
      {e.ai?.verdict && <VerdictChip ai={e.ai} size="sm" conf={false} />}
      {live.length > 0 ? (
        <span className="dwatch" title={live.map((w) => `AI watching ${w.cameraNames.join(', ')} for ${watchWhat(w)}`).join('\n')}>
          <Icon name="eye" className="eyepulse" />
          {live.some((w) => w.status === 'confirming') ? 'AI checking a hit' : [...new Set(live.map(watchWhat))].join(', ')}
          {wEnd !== null && <span className="mono"> {dur(wEnd - now)}</span>}
        </span>
      ) : hit ? (
        <span className="dwatch hit" title={`AI watch saw ${watchWhat(hit)}`}>
          <Icon name="eye" />
          saw {watchWhat(hit)}
        </span>
      ) : null}
      <span className="mono dim" title="Parked for">
        <Since from={e.park?.parkedAt ?? null} />
      </span>
      <span className={`mono${left !== null && left < 60_000 ? ' late' : ''}`} title="Fallback timer: raises back to you">
        {left === null ? '—' : left > 0 ? `↑ ${dur(left)}` : 'raising'}
      </span>
      <button type="button" className="btn sm" onClick={onResume} disabled={!canResume || busy} title={canResume ? 'Resume as an active event' : 'At your active cap'}>
        {busy ? '…' : 'Resume'}
      </button>
    </div>
  );
}

/** Parked events under the workspace: reason, parked-for, fallback countdown, Resume. */
export function ParkedDock({
  parked,
  openId,
  canResume,
  busyId,
  watchesOf,
  onOpen,
  onResume,
}: {
  parked: EventSummary[];
  openId: string | null;
  canResume: boolean;
  busyId: string | null;
  /** Phase 4: AI watches per parked event. */
  watchesOf?: (eventId: string) => Watch[] | undefined;
  onOpen: (e: EventSummary) => void;
  onResume: (e: EventSummary) => void;
}) {
  if (!parked.length) return null;
  return (
    <div className="pdock" aria-label="Parked events">
      <span className="lbl">
        <Icon name="park" />
        Parked · {parked.length}
      </span>
      <div className="pdock-list" role="list">
        {parked.map((e) => (
          <DockChip key={e.id} e={e} open={e.id === openId} canResume={canResume} busy={busyId === e.id} watches={watchesOf?.(e.id)} onOpen={() => onOpen(e)} onResume={() => onResume(e)} />
        ))}
      </div>
    </div>
  );
}

/* ---------- header ---------- */

function EventHeader({ c }: { c: Ctl }) {
  const ev = c.ev;
  const tz = ev.siteTimeZone;
  const now = useNow(1000);
  const last = ms(ev.lastAlarmAt);
  const handlingFrom = ev.acceptedAt ?? ev.openedAt;
  const slaStart = ms(ev.queuedAt) ?? ms(ev.firstAlarmAt);
  const sla = ms(ev.slaDueAt);
  return (
    <div className={`evhead s-${SEV_CLS[ev.severity]}`}>
      <div className="t">
        <h2>
          <span className="sev big">{SEV_LABEL[ev.severity]}</span>
          {ev.site.name}
          <span className="mono dim evno">{ev.displayNo}</span>
          {ev.ai?.verdict && <VerdictChip ai={ev.ai} />}
          <AiStatePill state={ev.state} />
          {ev.agentPaused && (
            <span className="pill" title="The AI agent is paused on this event">
              <Icon name="pause" />
              agent paused
            </span>
          )}
          {!ev.site.live && <span className="pill warn">commissioning</span>}
          {ev.storm && <span className="pill warn">alarm storm</span>}
        </h2>
      </div>
      <div className="kv">
        <div>
          <span className="lbl">Site time</span>
          <SiteClock tz={tz} />
        </div>
        <div>
          <span className="lbl">First alarm</span>
          <b>{fmtTime(ev.firstAlarmAt, tz)}</b>
          <span className="dim mono tiny">last {last === null ? '—' : `${dur(now - last)} ago`}</span>
        </div>
        <div>
          <span className="lbl">Handling</span>
          <Since tag="b" from={handlingFrom} lateAt={ev.slaDueAt} />
          <span className="dim mono tiny" title={ev.slaDueAt ? `SLA due ${fmtTime(ev.slaDueAt, tz)} ${tzAbbr(tz)}` : 'No SLA set'}>
            {sla !== null && slaStart !== null ? `SLA ${dur(sla - slaStart)}` : 'no SLA'}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------- split (from the timeline's alarm card) ---------- */

function SplitConfirm({ c, alarmId, onDone }: { c: Ctl; alarmId: string; onDone: () => void }) {
  const ev = c.ev;
  const sel = ev.alarms.find((a) => a.id === alarmId);
  if (!sel) return null;
  const n = ev.alarms.findIndex((a) => a.id === sel.id) + 1;
  const atCap = c.activeCount >= c.maxActive;
  const go = async (claim: boolean) => {
    // The new event shows in the tabs or the queue.
    await c.run('Split alarm', (v) => api.split(ev.id, [sel.id], claim, v), (res) => res.event);
    onDone();
  };
  return (
    <div className="confirmbar" role="group" aria-label="Split alarm">
      <span>
        Move <b>A{n}</b> ({camShort(sel.cameraName)} · {human(sel.analytic, 'Alarm')} · {fmtTime(alarmMs(sel), ev.siteTimeZone)}) into a new event?
        {atCap && <span className="note"> You are at {c.activeCount}/{c.maxActive}, so a kept event goes to the queue.</span>}
      </span>
      <button type="button" className="btn pri" onClick={() => void go(true)} disabled={!!c.busy}>
        Keep it with me
      </button>
      <button type="button" className="btn" onClick={() => void go(false)} disabled={!!c.busy}>
        Send to queue
      </button>
      <button type="button" className="btn" onClick={onDone}>
        Cancel
      </button>
    </div>
  );
}

/* ---------- Phase 5: push-to-talk on air (prototype .ptt-live) ---------- */

function PttLive({ c }: { c: Ctl }) {
  const p = c.ptt;
  const now = useNow(250);
  if (p.phase === 'idle' || !p.target || p.target.eventId !== c.ev.id) return null;
  const label = p.target.label.toUpperCase();
  // Live talk (no speaker devices: the camera's own speaker) is heard as it is spoken; the clip plays after release.
  const live = !p.target.deviceIds.length;
  if (live)
    return (
      <div className={`ptt-live p-${p.phase}`} role="status" aria-live="assertive">
        <Icon name="mic" />
        {p.phase === 'recording' ? (
          <>
            <i className="recdot" aria-hidden="true" />
            ON AIR · LIVE ON {label} · <span className="mono">{dur(now - (p.startedAt ?? now))}</span> · RELEASE T TO STOP
          </>
        ) : (
          <>CONNECTING TO {label} · KEEP HOLDING T · SPEAK NOW</>
        )}
      </div>
    );
  return (
    <div className={`ptt-live p-${p.phase}`} role="status" aria-live="assertive">
      <Icon name="mic" />
      {p.phase === 'recording' ? (
        <>
          <i className="recdot" aria-hidden="true" />
          REC · LIVE ON {label} · <span className="mono">{dur(now - (p.startedAt ?? now))}</span> · RELEASE T TO SEND · ESC CANCELS
        </>
      ) : p.phase === 'opening' ? (
        <>OPENING THE MICROPHONE · KEEP HOLDING T</>
      ) : (
        <>PLAYING ON {label}…</>
      )}
    </div>
  );
}

/* ---------- listen: the main camera's microphone (core talk.ts) ---------- */

/**
 * A toggle: the camera's microphone through an <audio> on a short-lived, audited URL. It ducks while the operator talks
 * (so the speaker's sound doesn't come back through the microphone), stops when the camera changes, and says why when
 * NX sends no audio (no microphone, or audio off in NX).
 */
function ListenButton({ c, cameraId, cameraName }: { c: Ctl; cameraId: string; cameraName: string }) {
  const [on, setOn] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLAudioElement | null>(null);
  const talking = c.ptt.phase === 'recording' || c.ptt.phase === 'opening';
  useEffect(() => {
    setOn(false);
    setUrl(null);
    setErr(null);
  }, [cameraId]);
  useEffect(() => {
    if (!on) return setUrl(null);
    let gone = false;
    api.audioListen(cameraId, c.ev.id).then(
      (r) => !gone && setUrl(r.url),
      (e: unknown) => {
        if (gone) return;
        setErr(errorText(e));
        setOn(false);
      },
    );
    return () => {
      gone = true;
    };
  }, [on, cameraId, c.ev.id]);
  useEffect(() => {
    const a = ref.current;
    if (a) a.muted = talking;
  }, [talking, url]);
  // Stop, a new camera or leaving the event ends the request, which ends the stream from NX or the EyeQ VMS. Removing
  // the <audio> alone doesn't: the browser keeps downloading a live stream until it collects the element, and the VMS
  // keeps the microphone open and one of its 32 stream slots taken (2026-11-01).
  useEffect(() => {
    const a = ref.current;
    if (!url || !a) return;
    return () => {
      a.pause();
      a.removeAttribute('src');
      a.load();
    };
  }, [url]);
  const failed = () => {
    if (!url) return;
    void fetch(url).then(
      async (r) => setErr(((await r.json().catch(() => null)) as { error?: string } | null)?.error ?? `No audio from ${camShort(cameraName)}.`),
      () => setErr(`No audio from ${camShort(cameraName)}.`),
    );
    setOn(false);
  };
  return (
    <>
      <button
        type="button"
        className={`btn${on ? ' on' : ''}`}
        aria-pressed={on}
        onClick={() => {
          setErr(null);
          setOn((x) => !x);
        }}
        title={err ?? (on ? `Stop listening to ${camShort(cameraName)}` : `Listen to ${camShort(cameraName)}'s microphone (audited)`)}
      >
        <Icon name={on ? 'speaker' : 'mute'} />
        {on ? 'Listening' : 'Listen'}
      </button>
      {err && (
        <span className="note warn" role="status">
          {err}
        </span>
      )}
      {url && <audio ref={ref} src={url} autoPlay onError={failed} aria-label={`Audio from ${cameraName}`} />}
    </>
  );
}

/* ---------- player (the main canvas: single, or quad over the alarm cameras) ---------- */

function Player({ c }: { c: Ctl }) {
  const v = c.view;
  const ranked = useMemo(() => rankCameras(c.ev, c.agent.data), [c.ev, c.agent.data]);
  const main = c.ev.cameras.find((x) => x.id === v.cameraId) ?? ranked[0]?.cam;
  // The quad's cameras by slot last render, so a new alarm doesn't reshuffle (and reload) the tiles.
  const slots = useRef<string[]>([]);
  const cams: EventCamera[] = v.layout === 'quad' ? quadCameras(ranked, v.cameraId, slots.current) : main ? [main] : [];
  if (v.layout === 'quad') slots.current = cams.map((x) => x.id);
  else if (main && !slots.current.includes(main.id)) slots.current = [main.id];
  // Feeds are keyed by quad slot. Single view keeps the main camera in its quad slot, so switching single / quad
  // leaves its feed (and stream) as it is.
  const slotOf = (cam: EventCamera, i: number) => (v.layout === 'quad' ? i : Math.max(0, slots.current.indexOf(cam.id)));
  // The timeline's playhead, which the other quad tiles keep to while reviewing recording (null when live).
  const view = useRef(v);
  view.current = v;
  const playhead = useCallback(() => (view.current.seekAt === null ? null : vtime(view.current, Date.now())), []);
  const at = toIso(v.seekAt);
  const paused = v.pauseStart !== null;
  const badge =
    v.mode === 'live' ? ({ text: 'LIVE', cls: 'live' } as const) : v.mode === 'alarm' ? ({ text: 'ALARM CLIP', cls: 'alarm' } as const) : ({ text: v.synced ? 'SYNC' : 'ARCHIVE', cls: 'archive' } as const);
  const camAlarmIds = new Set(c.ev.alarms.map((a) => a.cameraId));
  const keyOf = new Map(c.railCams.slice(0, 9).map((x, i) => [x.id, i + 1]));
  const quad = v.layout === 'quad';
  return (
    <div className="player">
      {c.features.deterrence && <PttLive c={c} />}
      <div className={`pgrid${quad ? ' quad' : ''}${quad && cams.length <= 2 ? ' q2' : ''}`}>
        {cams.length === 0 ? (
          <div className="pcell">
            <div className="feed off">
              <span className="nosig info">This site has no cameras in the replica.</span>
            </div>
          </div>
        ) : (
          cams.map((cam, i) => {
            const isMain = cam.id === v.cameraId;
            const pick = !isMain;
            return (
              <div
                // Keyed by slot, not camera: a camera change keeps the feed mounted, so its watch-time view
                // ends as `switched`; a tile that goes away (quad → single) ends as `closed`.
                key={slotOf(cam, i)}
                className={`pcell${isMain ? ' sel' : ''}${camAlarmIds.has(cam.id) ? ' alarmcam' : ''}`}
                onClick={() => pick && c.selectCamera(cam.id)}
                role={pick ? 'button' : undefined}
                tabIndex={pick ? 0 : undefined}
                aria-label={pick ? `Make ${cam.name} the main camera` : undefined}
                onKeyDown={(e) => pick && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), c.selectCamera(cam.id))}
              >
                <CameraFeed
                  cameraId={cam.id}
                  cameraName={cam.name}
                  at={at}
                  // Only the owner's viewing counts as the event's video review; read-only viewers are audited as plain video access.
                  eventId={c.canAct ? c.ev.id : null}
                  alarmClip={v.mode === 'alarm'}
                  paused={paused}
                  badge={badge}
                  offline={cam.health?.status === 'offline'}
                  nonce={v.nonce}
                  onRenew={c.renewVideo}
                  // The main feed's recorded stream running out carries the view on (live, or more recording).
                  onEnded={isMain ? c.videoEnded : undefined}
                  // The other quad tiles: NX's low-resolution stream, kept to the timeline's playhead, on into live like
                  // the main camera. Selecting one makes it the main camera, at full size.
                  tile={quad && !isMain}
                  epoch={v.epoch ?? 0}
                  playhead={quad && !isMain ? playhead : undefined}
                />
                {quad && keyOf.has(cam.id) && <span className="tnum">{keyOf.get(cam.id)}</span>}
              </div>
            );
          })
        )}
      </div>
      <div className="pctl">
        <div className="seg" role="radiogroup" aria-label="Video mode">
          {(
            [
              ['alarm', 'Alarm clip', 'A'],
              ['live', 'Live', 'L'],
              ['archive', 'Archive', ''],
            ] as const
          ).map(([m, l, k]) => (
            <button key={m} type="button" role="radio" aria-checked={v.mode === m} className={v.mode === m ? 'on' : ''} onClick={() => c.setMode(m)}>
              {l}
              {k && <kbd>{k}</kbd>}
            </button>
          ))}
        </div>
        <button type="button" className="btn icon" onClick={() => c.jump(-10)} title="Back 10 s" aria-label="Back 10 seconds">
          <Icon name="rew" />
        </button>
        <button type="button" className="btn icon" onClick={c.playPause} title="Play / pause (Space)" aria-label={paused ? 'Play' : 'Pause'}>
          <Icon name={paused ? 'play' : 'pause'} />
        </button>
        <button type="button" className="btn icon" onClick={() => c.jump(10)} title="Forward 10 s" aria-label="Forward 10 seconds" disabled={v.mode === 'live'}>
          <Icon name="fwd" />
        </button>
        <button type="button" className={`btn${v.synced ? ' on' : ''}`} onClick={() => c.toggleSync()} aria-pressed={v.synced} title="Quad of the alarm cameras, all at the selected alarm's time (the matrix follows too)">
          <Icon name="sync" />
          Sync all to alarm <kbd>S</kbd>
        </button>
        <span className="sp" />
        <span className="pcam mono" title={main?.name}>
          {camShort(main?.name)}
        </span>
        <div className="seg" role="radiogroup" aria-label="Layout">
          <button type="button" role="radio" aria-checked={v.layout === 'single'} className={v.layout === 'single' ? 'on' : ''} onClick={() => c.setLayout('single')} title="Single camera (Q)">
            <Icon name="single" />
          </button>
          <button type="button" role="radio" aria-checked={v.layout === 'quad'} className={v.layout === 'quad' ? 'on' : ''} onClick={() => c.setLayout('quad')} title="Quad: the event's alarm cameras, newest first (Q)">
            <Icon name="grid" />
          </button>
        </div>
        {c.features.deterrence && main && <ListenButton c={c} cameraId={main.id} cameraName={main.name} />}
        <button type="button" className="btn" onClick={c.openVideo} title="This canvas in its own window (for a second monitor), time-synced to this player">
          <Icon name="ext" />
          Pop out
        </button>
        <button type="button" className="btn" onClick={c.openMatrix} title={`All ${plural(c.ev.cameras.length, 'site camera')} in their own window, time-synced to this player (M)`}>
          <Icon name="grid" />
          Cameras <kbd>M</kbd>
        </button>
      </div>
    </div>
  );
}

/* ---------- workspace ---------- */

export function Workspace({ c, banner }: { c: Ctl; banner: ReactNode }) {
  const [splitting, setSplitting] = useState<string | null>(null);
  useEffect(() => setSplitting(null), [c.ev.id]);
  return (
    <div className="evbody">
      <EventHeader c={c} />
      {banner}
      {splitting && <SplitConfirm c={c} alarmId={splitting} onDone={() => setSplitting(null)} />}
      <div className="stage">
        <Player c={c} />
      </div>
      <Timeline c={c} onSplit={(id) => setSplitting((s) => (s === id ? null : id))} />
    </div>
  );
}

export function EmptyWorkspace({ children }: { children: ReactNode }) {
  return <div className="evbody empty">{children}</div>;
}
